import { Reducer } from 'react';
import { BulkActionsState } from '../../../models/bulk-actions';
import { BulkDispatchAction } from './BulkDispatchActions';
import { ImmutabilityContext } from './effects/customImmutabilityContext';
import { PostFeedType } from '../../../models/graphql';
import { IsKeyOfUtmInput } from './reducers/link.reducer';
import { EvergreenType } from '../../../models/post-editor';

export const BulkActionsReducer: Reducer<BulkActionsState, BulkDispatchAction> = (state, action) => {
  const { update: updateImmutable } = ImmutabilityContext;
  switch (action.type) {
    case 'resetInstagramPublishing':
      return updateImmutable(state, {
        targeting: {
          checkboxes: {
            instagramPublishMethod: { $set: false },
            instagramPostFeedType: { $set: false }
          },
          content: {
            instagramPublishMethod: { $set: null },
            instagramPostFeedType: { $set: PostFeedType.FEED }
          }
        }
      });
    case 'setBitly': {
      return updateImmutable(state, {
        linkSettings: {
          content: {
            bitly: { $set: action.payload }
          }
        }
      });
    }
    case 'setChanges': {
      const { value, section } = action.payload;
      return updateImmutable(state, {
        [section]: {
          hasChanges: {
            $set: value
          }
        }
      });
    }
    case 'setEvergreen': {
      return updateImmutable(state, {
        scheduling: {
          content: {
            evergreenRecycle: {
              active: { $set: action.payload }
            }
          }
        }
      });
    }
    case 'setExpiryDate': {
      return updateImmutable(state, {
        scheduling: {
          content: {
            evergreenRecycle: {
              expiryDate: { $set: action.payload }
            }
          }
        }
      });
    }
    case 'setExpiryMaxRepeats': {
      return updateImmutable(state, {
        scheduling: {
          content: {
            evergreenRecycle: {
              expiryMaxRepeats: { $set: action.payload }
            }
          }
        }
      });
    }
    case 'setExpiryType': {
      const { expireType, action: actionFromPayload } = action.payload;
      return updateImmutable(state, {
        scheduling: {
          content: {
            evergreenRecycle: {
              expireType: {
                $apply: (current: EvergreenType['expireType']) => {
                  const indexOfCurrent = current.indexOf(expireType);
                  if (actionFromPayload === 'remove' && indexOfCurrent > -1) {
                    current.splice(indexOfCurrent, 1);
                    return [...current];
                  }
                  if (actionFromPayload === 'add' && indexOfCurrent === -1) {
                    return [...current, expireType];
                  }
                  return current;
                }
              }
            }
          }
        }
      });
    }
    case 'setInstagramPostFeedType': {
      return updateImmutable(state, {
        targeting: {
          content: {
            instagramPostFeedType: { $set: action.payload }
          }
        }
      });
    }
    case 'setInstagramPublishingMethod': {
      return updateImmutable(state, {
        targeting: {
          content: {
            instagramPublishMethod: { $set: action.payload }
          }
        }
      });
    }
    case 'setKeepExisting': {
      return updateImmutable(state, {
        linkSettings: {
          content: {
            keepExistingUtm: { $set: action.payload }
          }
        }
      });
    }
    case 'setLinkDestination': {
      return updateImmutable(state, {
        linkSettings: {
          content: {
            linkDestination: { $set: action.payload }
          }
        }
      });
    }
    case 'setPositionInQueue': {
      return updateImmutable(state, {
        scheduling: {
          content: {
            positionInQueue: { $set: action.payload }
          }
        }
      });
    }
    case 'setPostStatus': {
      return updateImmutable(state, {
        targeting: {
          content: {
            postStatus: { $set: action.payload }
          }
        }
      });
    }
    case 'setSchedulingSettings': {
      const { evergreen, positionInQueue } = action.payload;
      return updateImmutable(state, {
        scheduling: {
          content: {
            evergreenRecycle: {
              active: { $set: evergreen }
            },
            positionInQueue: { $set: positionInQueue }
          }
        }
      });
    }
    case 'setUtmSettings': {
      return updateImmutable<BulkActionsState>(state, {
        linkSettings: {
          content: {
            utmSettings: current => {
              for (const [key, value] of Object.entries(action.payload)) {
                if (!IsKeyOfUtmInput(key)) return current;
                current = updateImmutable(current, {
                  [key]: { $set: value }
                });
              }
              return current;
            }
          }
        }
      });
    }
    case 'setUtmCampaignTracking': {
      return updateImmutable(state, {
        linkSettings: {
          content: {
            addUtmCampaignTracking: { $set: action.payload }
          }
        }
      });
    }
    case 'toggleCheckbox': {
      const { key, section } = action.payload;
      return updateImmutable(state, {
        [section]: {
          checkboxes: {
            [key]: {
              $apply: (current: boolean) => !current
            }
          }
        }
      });
    }
    case 'toggleEvergreen': {
      return updateImmutable(state, {
        scheduling: {
          content: {
            evergreenRecycle: {
              active: {
                $apply: (current: boolean) => !current
              }
            }
          }
        }
      });
    }
    case 'updateCategory': {
      return updateImmutable(state, {
        targeting: {
          content: {
            category: {
              $set: action.payload
            }
          }
        }
      });
    }
    case 'updateProfiles': {
      return updateImmutable(state, {
        targeting: {
          content: {
            profiles: {
              $set: action.payload
            }
          }
        }
      });
    }
    default:
      return state;
  }
};
